/**
 * writeQueue/queue.ts — public API for the editorial write queue.
 *
 * Higher layers (EditorialQueue.tsx, EditableField.tsx, etc.) interact
 * with the queue exclusively through the functions exported here. This
 * file wraps db.ts with:
 *   - UUID generation
 *   - React hooks (usePending, usePendingForRow) backed by useSyncExternalStore
 *   - A simple pub-sub for change notifications so hooks re-render on queue mutations
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
 *
 * Usage pattern (from a mutation handler):
 *
 *   import { enqueue } from '@/lib/writeQueue/queue';
 *
 *   async function handleApprove(draft: Draft) {
 *     // 1. Optimistic UI (unchanged from today)
 *     setDrafts(prev => prev.filter(d => d.id !== draft.id));
 *
 *     // 2. Durable enqueue. Returns immediately after IndexedDB write.
 *     await enqueue('approve_draft', {
 *       p_draft_id: draft.id,
 *       p_fast_track: fastTrack,
 *       // ...
 *     }, draft.id);
 *
 *     // 3. Sync worker (sync.ts) picks it up and dispatches with retry.
 *     // The handler does NOT await the network call directly.
 *   }
 */

import { useSyncExternalStore } from 'react';
import {
  MutationOp,
  MutationRecord,
  MutationStatus,
  putMutation,
  updateMutation,
  deleteMutation,
  listByStatus,
  listByTargetRow,
  countByStatus,
} from './db';

// ============================================================================
// Pub-sub for queue change notifications
// ============================================================================

type Listener = () => void;
const listeners = new Set<Listener>();

/** Internal — called by queue.ts and sync.ts whenever the queue mutates. */
export function notifyChange(): void {
  listeners.forEach(l => {
    try { l(); } catch (err) { console.error('[writeQueue] listener threw:', err); }
  });
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// ============================================================================
// UUID generation
// ============================================================================

function uuid(): string {
  // crypto.randomUUID is widely supported in modern browsers and Node 19+.
  // Fall back to a date+random hybrid if unavailable (won't happen in real
  // browsers we target, but keeps the type-check honest for SSR/test envs).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ============================================================================
// Public mutation API
// ============================================================================

/**
 * Durably enqueue a mutation. Returns the queue id once the IndexedDB
 * write resolves. The sync worker picks it up on its next tick and
 * dispatches with retry.
 *
 * The caller's optimistic UI should fire BEFORE enqueue resolves (i.e.,
 * synchronously in the click handler) so the editor sees instant feedback.
 * The await on enqueue is for durability — the click handler should
 * confirm to the user only after enqueue resolves successfully.
 *
 * If IndexedDB is unavailable (Safari private mode), enqueue rejects with
 * the underlying IndexedDbUnavailableError. The caller should catch this
 * and fall back to today's in-memory snapshot-restore behavior (and the
 * UI should already be warning the editor at startup that durability is
 * disabled via probeAvailability).
 */
export async function enqueue(
  op: MutationOp,
  payload: Record<string, unknown>,
  targetRowId: string,
): Promise<string> {
  const now = Date.now();
  const record: MutationRecord = {
    id: uuid(),
    op,
    payload,
    targetRowId,
    createdAt: now,
    attempts: 0,
    nextRetryAt: now, // immediately eligible — sync worker picks up on next tick
    lastError: null,
    status: 'pending',
  };
  await putMutation(record);
  notifyChange();
  return record.id;
}

/**
 * Mark a `failed_giveup` record back to `pending` so the sync worker
 * retries it. Resets `attempts` to 0 and `nextRetryAt` to now.
 */
export async function retry(id: string): Promise<void> {
  const records = await listByStatus('failed_giveup');
  const target = records.find(r => r.id === id);
  if (!target) return;
  target.status = 'pending';
  target.attempts = 0;
  target.nextRetryAt = Date.now();
  target.lastError = null;
  await updateMutation(target);
  notifyChange();
}

/** Retry every `failed_giveup` record. */
export async function retryAll(): Promise<void> {
  const records = await listByStatus('failed_giveup');
  const now = Date.now();
  for (const r of records) {
    r.status = 'pending';
    r.attempts = 0;
    r.nextRetryAt = now;
    r.lastError = null;
    await updateMutation(r);
  }
  notifyChange();
}

/** Discard a `failed_giveup` record — caller has decided not to retry. */
export async function discard(id: string): Promise<void> {
  await deleteMutation(id);
  notifyChange();
}

// ============================================================================
// React hooks
// ============================================================================
//
// Stability contract — read before editing this section:
//
// useSyncExternalStore uses Object.is(prev, next) to decide whether the
// store changed. Two bug classes have already bitten us:
//
//   1. Returning a NEW object reference with identical values on every
//      getSnapshot call → React detects infinite "changed" → maximum update
//      depth error (#185). Fixed in usePendingForRow via EMPTY_PER_ROW_STATE
//      singleton (2026-05-19 commit 8c27905).
//
//   2. Caching a snapshot object that includes a TIME-DERIVED field
//      (e.g. Date.now()) → every refresh produces a snapshot whose values
//      genuinely differ even when the underlying queue is unchanged →
//      React re-renders forever, plus the refresh closure was previously
//      registered as a listener and re-triggered itself in .finally(),
//      causing a CPU + IndexedDB cascade. Fixed 2026-05-19 by (a) replacing
//      the volatile `oldestPendingAgeMs` field with the stable
//      `oldestPendingCreatedAt` (callers compute age at render time), and
//      (b) removing the self-listening scheduleSnapshotRefresh — each hook
//      now owns its own refresh closure registered via subscribe(); none
//      of them re-trigger themselves.
//
// Invariants for any new hook in this file:
//   - getSnapshot returns a stable reference when underlying values
//     haven't changed.
//   - Cache assignments only happen when the new values differ from the
//     prior cache.
//   - No time-derived fields in cached snapshots. If a hook needs to
//     surface relative time, store the source timestamp and compute the
//     delta at render time.
//   - The refresh closure for a hook MAY call its React `listener` after
//     fetching, but the refresh closure MUST NOT trigger itself or any
//     other refresh closure recursively.

interface PendingSnapshot {
  pendingCount: number;
  inflightCount: number;
  failedGiveupCount: number;
  /**
   * Wall-clock ms-since-epoch when the oldest pending record was created.
   * Null when no pending records. Callers compute age = Date.now() - this
   * at render time; storing the AGE here would make every refresh look
   * like a value change and trigger unbounded re-renders.
   */
  oldestPendingCreatedAt: number | null;
}

// Snapshot cache so useSyncExternalStore can return a stable reference
// between calls. Mutated only when values actually change (see
// refreshSnapshot).
let snapshot: PendingSnapshot = {
  pendingCount: 0,
  inflightCount: 0,
  failedGiveupCount: 0,
  oldestPendingCreatedAt: null,
};

async function refreshSnapshot(): Promise<void> {
  const counts = await countByStatus();
  const pendingRecords = await listByStatus('pending');
  const oldestCreatedAt = pendingRecords.reduce<number | null>(
    (acc, r) => (acc === null || r.createdAt < acc ? r.createdAt : acc),
    null,
  );
  // Only mutate the snapshot REFERENCE when values actually change.
  // Returning a stable reference for unchanged state is the contract
  // useSyncExternalStore relies on to skip re-renders.
  if (
    snapshot.pendingCount !== counts.pending ||
    snapshot.inflightCount !== counts.inflight ||
    snapshot.failedGiveupCount !== counts.failed_giveup ||
    snapshot.oldestPendingCreatedAt !== oldestCreatedAt
  ) {
    snapshot = {
      pendingCount: counts.pending,
      inflightCount: counts.inflight,
      failedGiveupCount: counts.failed_giveup,
      oldestPendingCreatedAt: oldestCreatedAt,
    };
  }
}

/**
 * Hook returning aggregate queue state. Re-renders the subscribing
 * component when the queue's count or oldest-pending timestamp changes;
 * stable-reference returns from getSnapshot when nothing has changed.
 *
 * Drives the chrome indicator (Item #3 in the rebuild plan).
 */
export function usePending(): PendingSnapshot {
  return useSyncExternalStore(
    (listener) => {
      const refresh = async () => {
        try {
          await refreshSnapshot();
        } catch (err) {
          console.warn('[writeQueue.usePending] refresh failed:', err);
        }
        // Always notify React — getSnapshot will return the SAME snapshot
        // reference if nothing changed, so Object.is correctly short-
        // circuits the re-render. This avoids needing to track changes
        // here.
        listener();
      };
      const unsub = subscribe(refresh);
      refresh();
      return unsub;
    },
    () => snapshot,
    () => snapshot,
  );
}

// ----------------------------------------------------------------------------
// usePendingList — full list of pending/inflight/failed records for the panel
// ----------------------------------------------------------------------------

let pendingListCache: MutationRecord[] = [];

/**
 * Length+id signature for quick "did the list change?" check. Avoids
 * deep-comparing every record on every refresh. False positives (list
 * looks unchanged but a record's status flipped) are caught by the next
 * refresh — UI lag is at most one tick.
 */
function pendingListSignature(list: MutationRecord[]): string {
  return list.map(r => `${r.id}:${r.status}:${r.attempts}`).join('|');
}
let pendingListSig = '';

async function refreshPendingList(): Promise<void> {
  const pending = await listByStatus('pending');
  const inflight = await listByStatus('inflight');
  const failed = await listByStatus('failed_giveup');
  const all = [...pending, ...inflight, ...failed];
  all.sort((a, b) => a.createdAt - b.createdAt);
  const sig = pendingListSignature(all);
  if (sig !== pendingListSig) {
    pendingListSig = sig;
    pendingListCache = all;
  }
}

export function usePendingList(): MutationRecord[] {
  return useSyncExternalStore(
    (listener) => {
      const refresh = async () => {
        try {
          await refreshPendingList();
        } catch (err) {
          console.warn('[writeQueue.usePendingList] refresh failed:', err);
        }
        listener();
      };
      const unsub = subscribe(refresh);
      refresh();
      return unsub;
    },
    () => pendingListCache,
    () => pendingListCache,
  );
}

// ----------------------------------------------------------------------------
// usePendingForRow — per-row syncing/failed state for inline badges
// ----------------------------------------------------------------------------

const perRowSnapshot = new Map<string, { pending: boolean; status: MutationStatus | null }>();

/**
 * Shared singleton for "this row has no pending mutations." Reused on
 * every fallback read so Object.is returns true and React doesn't re-
 * render. Critical for performance — a typical queue page mounts 100+
 * RowSyncingBadge instances. (Fix history: commit 8c27905 originally
 * introduced this singleton to break a maximum-update-depth crash.)
 */
const EMPTY_PER_ROW_STATE: { pending: boolean; status: MutationStatus | null } = {
  pending: false,
  status: null,
};

export function usePendingForRow(rowId: string): {
  pending: boolean;
  status: MutationStatus | null;
} {
  const get = () => perRowSnapshot.get(rowId) ?? EMPTY_PER_ROW_STATE;
  return useSyncExternalStore(
    (listener) => {
      const refresh = async () => {
        try {
          const records = await listByTargetRow(rowId);
          const active = records.find(r => r.status === 'pending' || r.status === 'inflight' || r.status === 'failed_giveup');
          const nextPending = !!active;
          const nextStatus = active ? active.status : null;
          const prev = perRowSnapshot.get(rowId);
          // Only mutate the map entry (and notify React) when the row's
          // pending/status actually changed. Avoids new-reference churn
          // when a queue change elsewhere doesn't affect this row.
          if (!prev || prev.pending !== nextPending || prev.status !== nextStatus) {
            if (!nextPending) {
              // Falling back to "no pending" → delete the entry so the
              // shared EMPTY_PER_ROW_STATE singleton is returned. Keeps
              // the map small for queues with high row turnover.
              perRowSnapshot.delete(rowId);
            } else {
              perRowSnapshot.set(rowId, { pending: nextPending, status: nextStatus });
            }
            listener();
          }
        } catch (err) {
          console.warn('[writeQueue.usePendingForRow] refresh failed:', err);
        }
      };
      const unsub = subscribe(refresh);
      refresh();
      return unsub;
    },
    get,
    get,
  );
}
